import { useCallback, useEffect, useState } from 'react';
import {
  api, ErreurApi,
  type ChronologieParcelle, type EtatPhase, type PhaseProcedure, type ProcedureParcelle,
} from './api';
import { FormeTraitementAnomalie } from './anomalies';
import { Icone } from './icones';

/**
 * LA CHRONOLOGIE JURIDIQUE D'UNE PARCELLE — §9, §14, §31.
 *
 * ⚠️ CE QUI MANQUAIT N'ÉTAIT PAS UN ÉCRAN, C'ÉTAIT LE CHEMIN. La fiche
 * parcelle montrait les droits, les restrictions, la filiation et le journal —
 * l'ÉTAT du fonds — et rien de ce qui lui ARRIVE : aucun lien vers l'acte en
 * cours, le dossier qui l'instruit, l'anomalie qui le bloque. Un agent qui
 * ouvrait une parcelle pour suivre une donation devait connaître la référence
 * de la mutation et changer d'écran. Le parcours demandé — ouvrir une parcelle,
 * voir son état juridique, ouvrir la donation, suivre les étapes, résoudre une
 * anomalie, valider, voir l'effet sur le titre — n'avait aucune porte d'entrée.
 *
 * ⚠️ LES SIX ÉTATS DE PHASE SONT DESSINÉS DIFFÉREMMENT, ET AUCUN NE SE REPLIE
 * SUR UN AUTRE. « Sans objet » (une donation ne publie pas) et « à faire » se
 * ressemblent sur un écran mal fait : le premier n'attend rien, le second
 * attend quelqu'un. Les peindre pareil ferait attendre un geste impossible, ou
 * ignorer un geste dû. `non_observable` est un aveu, pas un état : le registre
 * ne peut pas le savoir.
 *
 * ⚠️ AUCUNE PHRASE N'EST COMPOSÉE ICI. `lecture`, `prochaine_action` et les
 * blocages viennent du SERVICE. Un écran qui déduirait « bloqué » de champs
 * bruts porterait une seconde version de la règle — celle qu'on ne teste pas.
 */

const LIBELLE_ETAT: Record<EtatPhase, string> = {
  faite: 'faite',
  en_cours: 'en cours',
  bloquee: 'bloquée',
  a_faire: 'à faire',
  sans_objet: 'sans objet',
  non_observable: 'non observable',
};

function dateFr(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('fr-FR');
}

export function ChronologieJuridique({
  identifiant, peutValider, peutTraiterAnomalie, onOuvrirDossier,
}: {
  identifiant: string;
  /** fon.mutation:valider — sans elle le service refusera l'avis. */
  peutValider: boolean;
  /** cad.controle:traiter — sans elle la décision est refusée. */
  peutTraiterAnomalie: boolean;
  onOuvrirDossier?: (reference: string) => void;
}) {
  const [c, setC] = useState<ChronologieParcelle | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setC(await api.parcelles.procedures(identifiant));
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les procédures de cette parcelle."
        : String((e as Error)?.message ?? e));
    }
  }, [identifiant]);

  useEffect(() => { void charger(); }, [charger]);

  if (erreur) return <p className="erreur" role="alert">{erreur}</p>;
  if (!c) return <p className="attente">Chargement des procédures…</p>;

  const ej = c.etat_juridique;
  const masque = !Array.isArray(ej.titulaires_actuels);

  return (
    <section className="chrono-juridique">
      {message && <p className="succes" role="status">{message}</p>}

      {/* ── L'ÉTAT JURIDIQUE, EN TÊTE ─────────────────────────────────────
          Ce que le fonds EST aujourd'hui, avant tout ce qui lui arrive. */}
      <h3>État juridique <span className="aide">au {dateFr(new Date().toISOString())}</span></h3>
      <p className={ej.anomalies.bloquantes > 0 ? 'avertissement danger' : 'mention'}>{ej.lecture}</p>

      {masque ? (
        <div className="masque">
          <strong>{(ej.titulaires_actuels as any).nombre} droit(s) actif(s) — titulaires masqués</strong>
          <p>Motif : <code>{(ej.titulaires_actuels as any).motif}</code>. La parcelle existe et porte des droits.</p>
        </div>
      ) : (ej.titulaires_actuels as any[]).length > 0 ? (
        <table className="tableau">
          <thead><tr><th>Titulaire</th><th>Nature</th><th>Quotité</th><th>Depuis</th><th>Titre foncier</th></tr></thead>
          <tbody>
            {(ej.titulaires_actuels as any[]).map((t, i) => (
              <tr key={i}>
                <td>{t.denomination} <span className="aide">{t.type}</span></td>
                <td>{t.nature}</td>
                {/* Fractions ENTIÈRES : 1/3 s'affiche 1/3, jamais 0,333. */}
                <td><code>{t.quotite}</code></td>
                <td>{dateFr(t.depuis)} <span className="aide">{t.inscrit_par}</span></td>
                <td>
                  {t.titre ? <code>{t.titre}</code>
                    /* ⚠️ UN DROIT SANS TITRE EST UNE INFORMATION, PAS UN VIDE.
                       Mesuré sur la donation du registre : le donataire est
                       inscrit SANS titre alors que le donateur en avait un. Une
                       case vide se lirait « défaut d'affichage ». */
                    : <span className="etat ambre" title={t.sans_titre}>aucun titre</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {ej.titres.length > 0 && (
        <ul className="liste-fine">
          {ej.titres.map((t) => (
            <li key={t.identifiant}>
              <code>{t.identifiant}</code> — {t.type}, émis le {dateFr(t.date_emission)} par {t.autorite}
              {' '}<span className={`etat ${t.statut === 'valide' ? 'vert' : 'rouge'}`}>{t.statut}</span>
            </li>
          ))}
        </ul>
      )}

      {ej.restrictions.length > 0 && (
        <p className="mention">
          {ej.restrictions.length} restriction(s) en vigueur : {ej.restrictions.map((r) => r.type).join(', ')}.
          Elles n'éteignent pas le droit, elles le limitent.
        </p>
      )}
      {ej.litiges.length > 0 && (
        <div className="avertissement danger">
          {ej.litiges.length} litige(s) non clos : {ej.litiges.map((l) => `${l.reference} (${l.statut})`).join(', ')}.
        </div>
      )}

      {/* ── LES PROCÉDURES ───────────────────────────────────────────────── */}
      <h3>Procédures <span className="aide">({c.procedures.length})</span></h3>
      {c.lecture && <p className="rien-a-signaler">{c.lecture}</p>}

      {c.procedures.map((p) => (
        <Procedure key={p.reference} p={p}
                   peutValider={peutValider} peutTraiterAnomalie={peutTraiterAnomalie}
                   onOuvrirDossier={onOuvrirDossier}
                   onFait={(m) => { setMessage(m); void charger(); }} />
      ))}

      {/* ── LE JOURNAL ───────────────────────────────────────────────────
          ⚠️ IL NE DIT PAS LA MÊME CHOSE QUE LES PHASES, et les deux sont
          rendus : les phases disent OÙ EN EST la procédure, le journal dit CE
          QUI A ÉTÉ FAIT. Il est en ajout seul et chaîné par empreinte — c'est
          lui qui fait foi si les deux se contredisent. */}
      {c.chronologie.length > 0 && (
        <>
          <h3>Journal <span className="aide">(§47 — ajout seul, scellé ; il fait foi)</span></h3>
          <ol className="chronologie">
            {c.chronologie.map((e, i) => (
              <li key={i}>
                <time>{new Date(e.horodatage).toLocaleString('fr-FR')}</time>
                <strong>{e.action}</strong>
                <span className="aide"> {e.objet_type}{e.acteur ? ` · ${e.acteur}` : ''}</span>
                {e.motif && <p>{e.motif}</p>}
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

function Procedure({
  p, peutValider, peutTraiterAnomalie, onOuvrirDossier, onFait,
}: {
  p: ProcedureParcelle;
  peutValider: boolean; peutTraiterAnomalie: boolean;
  onOuvrirDossier?: (reference: string) => void;
  onFait: (message: string) => void;
}) {
  const [ouverte, setOuverte] = useState<string | null>(
    // La phase qui attend quelque chose s'ouvre d'elle-même : c'est celle que
    // l'agent vient voir. Une accordéon toute fermée fait chercher.
    p.phases.find((f) => f.etat === 'bloquee')?.code
    ?? p.phases.find((f) => f.etat === 'en_cours')?.code ?? null);

  return (
    <article className={p.blocages.length ? 'proc bloquee' : 'proc'}>
      <header className="proc-tete">
        <div>
          <span className="fe-etat">{p.origine === 'dossier' ? 'dossier' : 'acte hors dossier'}</span>
          <h4>
            {p.libelle} <code>{p.reference}</code>
            <span className={`etat ${p.statut === 'validee' ? 'vert' : p.statut === 'rejetee' ? 'rouge' : 'ambre'}`}>
              {p.statut}
            </span>
          </h4>
          <p className="aide">
            Ouverte le {dateFr(p.ouverte_le)}
            {p.acte ? ` · acte ${p.acte.reference} (${p.acte.type})` : ' · aucun acte déposé'}
            {p.demande ? ` · demande ${p.demande.reference}` : ''}
          </p>
        </div>
        {p.affaire && onOuvrirDossier && (
          <button className="bouton discret" onClick={() => onOuvrirDossier(p.affaire!.reference)}>
            Ouvrir le dossier <Icone nom="ouvrir" />
          </button>
        )}
      </header>

      {p.hors_dossier && <p className="mention">{p.hors_dossier}</p>}

      {p.blocages.length > 0 && (
        <div className="avertissement danger">
          <strong>Ce qui empêche d'avancer</strong>
          <ul className="liste-fine">{p.blocages.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}

      {/* ⚠️ LE RUBAN DIT LA CHAÎNE ENTIÈRE, y compris ce qui est sans objet.
          Masquer les phases sans objet raccourcirait la chaîne et ferait croire
          qu'une donation suit le même parcours qu'une immatriculation. */}
      <div className="ruban-circuit">
        {p.phases.map((f, i) => (
          <button key={f.code}
                  className={`rc-etape ph-${f.etat}${ouverte === f.code ? ' courante' : ''}`}
                  onClick={() => setOuverte(ouverte === f.code ? null : f.code)}
                  title={f.lecture}>
            <span className="rc-n">{i + 1}</span>
            <span className="rc-nom">{f.libelle}</span>
            <span className="ph-etat">{LIBELLE_ETAT[f.etat]}</span>
          </button>
        ))}
      </div>

      {p.phases.filter((f) => ouverte === f.code).map((f) => (
        <DetailPhase key={f.code} f={f} p={p}
                     peutValider={peutValider} peutTraiterAnomalie={peutTraiterAnomalie}
                     onFait={onFait} />
      ))}

      {p.prochaine_action && (
        <p className="proc-suite"><strong>Prochaine action</strong> — {p.prochaine_action}</p>
      )}
    </article>
  );
}

function DetailPhase({
  f, p, peutValider, peutTraiterAnomalie, onFait,
}: {
  f: PhaseProcedure; p: ProcedureParcelle;
  peutValider: boolean; peutTraiterAnomalie: boolean;
  onFait: (message: string) => void;
}) {
  const [enTraitement, setEnTraitement] = useState<string | null>(null);
  const [avis, setAvis] = useState(false);

  return (
    <div className={`ph-detail ph-${f.etat}`}>
      <div className="ph-entete">
        <strong>{f.libelle}</strong>
        <span className="ph-etat">{LIBELLE_ETAT[f.etat]}</span>
        {f.le && <span className="fil-date">{dateFr(f.le)}</span>}
        {f.responsable && <span className="pm-qui">{f.responsable}</span>}
      </div>

      <p className="pm-quoi">{f.lecture}</p>

      {/* Ce que la règle a pu regarder : le canari, à l'écran. */}
      {f.couverture && f.couverture.regles_aveugles.length > 0 && (
        <p className="mention">
          Règle(s) ayant conclu sans rien comparer : <strong>{f.couverture.regles_aveugles.join(', ')}</strong>.
          Sur ce fonds, ces règles n'ont pas été éprouvées.
        </p>
      )}

      {f.echeance && (
        <p className={f.echeance.jours_restants < 0 ? 'erreur' : 'mention'}>
          {f.echeance.jours_restants < 0
            ? `Délai dépassé de ${-f.echeance.jours_restants} jour(s)`
            : `${f.echeance.jours_restants} jour(s) restants`} — échéance {dateFr(f.echeance.le)}.
          <span className="aide"> {f.echeance.fondement}</span>
        </p>
      )}

      {f.pieces.length > 0 && (
        <>
          <span className="pm-qui">Pièces ({f.pieces.length})</span>
          <ul className="liste-fine">
            {f.pieces.map((x) => (
              <li key={x.id}><code>{x.nom}</code>{x.role ? ` — ${x.role}` : ''} · {dateFr(x.le)}</li>
            ))}
          </ul>
        </>
      )}

      {f.decisions.length > 0 && (
        <>
          <span className="pm-qui">Décisions ({f.decisions.length})</span>
          <ul className="liste-fine">
            {f.decisions.map((d, i) => (
              <li key={i}>
                {d.quoi}{d.par ? ` — ${d.par}` : ''}{d.le ? ` · ${dateFr(d.le)}` : ''}
                {d.commentaire && <span className="fil-note">{d.commentaire}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ── LES ANOMALIES DE LA PHASE, RÉSOLVABLES ICI ──────────────────
          ⚠️ SUR PLACE, PAS DANS UN AUTRE ÉCRAN. L'agent qui suit une donation
          bloquée doit pouvoir lever le blocage sans perdre le fil : c'est le
          parcours demandé (« résoudre une anomalie → valider »). Le formulaire
          est le MÊME composant que la file d'anomalies — deux formulaires pour
          la même décision auraient divergé sur la longueur de motif exigée. */}
      {f.anomalies.length > 0 && (
        <>
          <span className="pm-qui">Anomalies ({f.anomalies.length})</span>
          <ul className="liste-fine">
            {f.anomalies.map((a) => (
              <li key={a.id}>
                <span className={`etat ${a.bloquante ? 'rouge' : 'ambre'}`}>
                  {a.bloquante ? 'bloquante' : `gravité ${a.gravite ?? '—'}`}
                </span>
                {' '}{a.libelle} <code className="brut">{a.id.slice(0, 8)}</code>
                {peutTraiterAnomalie ? (
                  <button className="bouton discret" onClick={() => setEnTraitement(a.id)}>Traiter</button>
                ) : (
                  <span className="masque"> décision refusée : <code>cad.controle:traiter</code> manquante</span>
                )}
                {enTraitement === a.id && (
                  <FormeTraitementAnomalie
                    anomalie={{ id: a.id, regle: a.regle, explication: null }}
                    onAnnuler={() => setEnTraitement(null)}
                    onFait={(m) => { setEnTraitement(null); onFait(m); }} />
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ── DONNER SON AVIS, DEPUIS LA PHASE VALIDATION ─────────────────
          Le service refusera si une anomalie bloquante reste ouverte, si le
          déposant est l'appelant, ou si l'autorité manque — et le refus le dit.
          On n'anticipe donc AUCUNE de ces règles ici : deux listes des mêmes
          empêchements divergent, et l'agent verrait un bouton ouvert qui refuse
          ou fermé sans raison. */}
      {f.code === 'validation' && p.acte
        && !['validee', 'rejetee', 'annulee'].includes(p.acte.statut) && (
        peutValider ? (
          avis ? (
            <FormeAvis reference={p.acte.reference}
                       onAnnuler={() => setAvis(false)}
                       onFait={(m) => { setAvis(false); onFait(m); }} />
          ) : (
            <button className="bouton" onClick={() => setAvis(true)}>Donner mon avis sur cet acte</button>
          )
        ) : (
          <p className="masque">
            Votre rôle ne permet pas de valider (<code>fon.mutation:valider</code>).
          </p>
        )
      )}

      {f.prochaine_action && <p className="proc-suite">{f.prochaine_action}</p>}
    </div>
  );
}

/**
 * L'AVIS SUR UN ACTE — approbation ou rejet, motivé.
 *
 * ⚠️ CE N'EST PAS UN `window.prompt`. L'écran Mutations en employait un : il
 * n'affiche pas les règles, n'oblige à rien, et perd la saisie si l'agent
 * clique à côté. Un avis sur un acte d'État se donne dans un formulaire.
 */
function FormeAvis({ reference, onFait, onAnnuler }: {
  reference: string; onFait: (m: string) => void; onAnnuler: () => void;
}) {
  const [decision, setDecision] = useState<'approuve' | 'rejete'>('approuve');
  const [commentaire, setCommentaire] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null);
    if (decision === 'rejete' && commentaire.trim().length < 3) {
      setErreur('Un rejet sans motif ne peut pas être enregistré : il est notifié au demandeur.');
      return;
    }
    setEnCours(true);
    try {
      const r = await api.mutations.valider(reference, { decision, commentaire: commentaire.trim() });
      onFait(r.message ?? `${reference} → ${r.statut} (${r.approbations} approbation(s)).`);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <form className="traitement-anomalie" onSubmit={(e) => void soumettre(e)}>
      <p className="aide">
        Règle des quatre yeux : deux comptes distincts doivent approuver, et le déposant n'en est jamais un.
      </p>
      <div className="tr-choix">
        <label className={decision === 'approuve' ? 'tr-option actif' : 'tr-option'}>
          <input type="radio" name={`a-${reference}`} checked={decision === 'approuve'}
                 disabled={enCours} onChange={() => setDecision('approuve')} />
          <span><strong>Avis favorable</strong><em>Au nom de l'autorité à laquelle votre compte est rattaché.</em></span>
        </label>
        <label className={decision === 'rejete' ? 'tr-option actif' : 'tr-option'}>
          <input type="radio" name={`a-${reference}`} checked={decision === 'rejete'}
                 disabled={enCours} onChange={() => setDecision('rejete')} />
          <span><strong>Rejet</strong><em>Motif obligatoire : il est notifié au demandeur.</em></span>
        </label>
      </div>
      <label className="champ">
        <span className="champ-label">
          Commentaire {decision === 'rejete' ? <span className="requis">obligatoire</span> : <span className="aide">(consigné au journal)</span>}
        </span>
        <textarea value={commentaire} rows={3} disabled={enCours}
                  onChange={(e) => setCommentaire(e.target.value)} />
      </label>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="actions-ligne">
        <button type="button" className="bouton discret" onClick={onAnnuler} disabled={enCours}>Annuler</button>
        <button type="submit" className="bouton" disabled={enCours}>
          {enCours ? 'Enregistrement…' : 'Enregistrer mon avis'}
        </button>
      </div>
    </form>
  );
}
